import type { CashAccountBalance, CashCount } from '@/api/cash';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { hueClasses } from '@/components/hues';
import { accountHue, accountState, type AccountState } from '@core/cash/board';
import { formatAmount } from '@core/format/money';
import { kindIcon } from './accountIcons';

/**
 * Count &amp; check — the fourth verb of the accounts board (design canvas, direction A): count a till
 * or petty cash, or reconcile a bank against its statement. One tile per account, each saying where
 * it stands; choosing one opens the count or the reconcile page for it.
 */
export function CountAndCheckPage({
  countable,
  banks,
  counts,
  reconciledTo,
  today,
  onCancel,
  onCount,
  onReconcile,
}: {
  countable: CashAccountBalance[];
  banks: CashAccountBalance[];
  counts: CashCount[] | undefined;
  reconciledTo: (accountId: number) => string | undefined;
  today: string;
  onCancel: () => void;
  onCount: (accountId: number) => void;
  onReconcile: (accountId: number) => void;
}) {
  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      <PageHeading
        title="Count & check"
        subtitle="Count what is in a drawer, or match a bank against its statement"
        actions={
          <Button onClick={onCancel} shortcut="back">
            Back
          </Button>
        }
      />

      <Group title="Count the cash" empty="No till or petty cash to count yet.">
        {countable.map((account) => (
          <Tile
            key={account.id}
            account={account}
            state={accountState(account, { counts, reconciledTo: null, today }, '₹')}
            action="Count"
            onChoose={() => onCount(account.id)}
          />
        ))}
      </Group>

      <Group title="Reconcile a bank" empty="No bank account yet.">
        {banks.map((account) => (
          <Tile
            key={account.id}
            account={account}
            state={accountState(
              account,
              { counts, reconciledTo: reconciledTo(account.id), today },
              '₹',
            )}
            action="Reconcile"
            onChoose={() => onReconcile(account.id)}
          />
        ))}
      </Group>
    </div>
  );
}

function Group({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}) {
  return (
    <Card>
      <p className="mb-3 text-sm font-semibold text-gray-900">{title}</p>
      {children.length === 0 ? (
        <EmptyState icon="icon icon-wallet" title={empty} />
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
      )}
    </Card>
  );
}

function Tile({
  account,
  state,
  action,
  onChoose,
}: {
  account: CashAccountBalance;
  state: AccountState;
  action: string;
  onChoose: () => void;
}) {
  // Petty cash's line already says what it holds ("₹1,850 of ₹3,000"); everything else leads with it.
  const line =
    state.progress !== null ? state.text : `₹${formatAmount(account.closing)} · ${state.text}`;

  const hue = hueClasses[accountHue[account.kind]];

  return (
    <button
      type="button"
      onClick={onChoose}
      className="border-border-color hover:bg-light flex cursor-pointer items-center gap-3 rounded-md border bg-white p-3 text-start"
    >
      <span
        className={`flex size-10 shrink-0 items-center justify-center rounded-md ${hue.wash} ${hue.ink}`}
      >
        <i className={kindIcon[account.kind]} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-gray-900">{account.name}</span>
        <span className="text-default block text-[length:var(--scale-list-meta)]">{line}</span>
      </span>
      <span className="text-primary text-[12px] font-semibold">{action}</span>
    </button>
  );
}
